import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';

import { api } from 'src/api/api-client.config';
import { env } from 'src/config/env';
import { TEAMS } from 'src/constants/teams';
import { createBlobInContainer, getBlobsInContainer } from 'src/api/filesApi';

import Iconify from '../iconify';

const getTeamId = (teamAbbr: string): number => {
  let teamData = TEAMS.find((team) => team?.abbr === teamAbbr);
  return !teamData ? 0 : teamData.teamId;
};

interface AssetsDialogProps {
  onSuccess: (
    assetUrl: string,
    title: string | null,
    titleFr: string | null,
    teamName: string | null,
    teamNameFr: string | null,
    jersey_number: number | null,
    teamLogo: string | null,
    teamId: number | null,
    playerId: number | null
  ) => void;
  onClose: () => void;
}

export default function AssetsDialog({
  onClose,
  onSuccess,
}: AssetsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [showAssetsType, setShowAssetsType] = useState<string | null>(null);
  const [teamPlayers, setTeamPlayers] = useState<any>([]);
  const [imageLibraryFiles, setImageLibraryFiles] = useState<
    { blobName: string; blobUrl: string }[]
  >([]);

  const onCancel = () => {
    setShowAssetsType(null);
    onClose();
  };

  const uploadFiles = async (event: any) => {
    if (event.target.files.length > 0) {
      setLoading(true);
      const files = event.target.files;
      let uploadPromises = [];
      for (let i = 0; i < files.length; i++) {
        uploadPromises.push(createBlobInContainer(files[i]));
      }
      if (uploadPromises.length > 0) {
        let link;
        const uploadFiles = async () => {
          await Promise.all(uploadPromises);

          link = `https://${env.STORAGERESOURCENAME}.blob.core.windows.net/${env.STORAGE_CONTAINER}/${files[0].name}`;
          // link =  `https://low6nhlbracketsassetsdev.blob.core.windows.net/assets/MicrosoftTeams-image%20(22).png`
          setLoading(false);
        };

        await uploadFiles().catch(console.error);
        addAssetHandler(link);
      }
    }
  };

  const addAssetHandler = (
    assetUrl = '',
    title = '',
    titleFr = '',
    jersey_number = null,
    teamId = 0,
    playerId = null
  ) => {
    // Add a jersey number and club name as description for Pepsi
    if (teamId !== 0 && showAssetsType === 'players') {
      const teamData = TEAMS.find((team) => team.teamId === teamId);

      onSuccess(
        assetUrl,
        title,
        titleFr,
        teamData?.teamName ?? '',
        teamData?.teamNameFr ?? '',
        jersey_number,
        `https://allstarvotefilesde.blob.core.windows.net/nhl-team-logos/${teamData?.teamId}.svg`,
        teamData?.teamId ?? 0,
        playerId
      );
      return;
    }

    onSuccess(
      assetUrl,
      title,
      titleFr,
      null,
      null,
      null,
      null,
      teamId,
      playerId
    );
  };

  const loadTeamPlayers = (teamAbbr: string) => {
    setLoading(true);
    console.log('teamId', teamAbbr);
    api
      .get<void, { squad: { Teams: any[] } }>(
        `/hockey/tournament/1/teams?token=${env.FEED_TOKEN}`
      )
      .then((data) => {
        let team = data.squad.Teams.find((team) => team.Abbr === teamAbbr);
        team.Players = team.Players.sort((p1: any, p2: any) =>
          p1.FirstName > p2.FirstName ? 1 : p1.FirstName < p2.FirstName ? -1 : 0
        );
        setTeamPlayers(team);

        setShowAssetsType('players');
        setLoading(false);
      });
  };

  const loadImageLibraryFiles = () => {
    setLoading(true);
    const fetchImages = async () => {
      const data = await getBlobsInContainer();
      setImageLibraryFiles(data);
      setShowAssetsType('image_library');
      setLoading(false);
    };

    fetchImages().catch(console.error);
  };

  const sortedTeams = TEAMS.sort((p1, p2) =>
    p1.teamName > p2.teamName ? 1 : p1.teamName < p2.teamName ? -1 : 0
  );

  return (
    <>
      <Dialog
        open={true}
        onClose={() => {}}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Add Asset</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: '100px 200px' }}
            >
              <CircularProgress />
            </Box>
          ) : showAssetsType === null ? (
            <Grid container sx={{ minWidth: 400 }}>
              <Grid item xs={3} sx={{ cursor: 'pointer', p: 1 }}>
                <Grid container>
                  <Grid
                    xs={12}
                    item
                    sx={{
                      height: 120,
                      width: 120,
                      textAlign: 'center',
                      p: 4,
                    }}
                  >
                    <img
                      alt='Remy Sharp'
                      src='/assets/icons/navbar/image-outline.svg'
                      onClick={() => loadImageLibraryFiles()}
                    />
                  </Grid>
                  <Grid
                    sx={{ fontSize: 14, textAlign: 'center', width: '100%' }}
                  >
                    Image Library
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3} sx={{ cursor: 'pointer', p: 1 }}>
                <Grid container>
                  <Grid
                    xs={12}
                    item
                    sx={{
                      height: 120,
                      width: 120,
                      textAlign: 'center',
                      p: 4,
                    }}
                  >
                    <input
                      accept='image/*'
                      type='file'
                      id='select-icon'
                      style={{ display: 'none' }}
                      onChange={async (e) => await uploadFiles(e)}
                    />
                    <label htmlFor='select-icon'>
                      <Iconify
                        icon='eva:plus-circle-outline'
                        sx={{
                          width: 20,
                          height: 20,
                          cursor: 'pointer',
                          marginTop: 2,
                          marginBottom: 2,
                        }}
                      />
                    </label>
                  </Grid>
                  <Grid
                    sx={{ fontSize: 14, textAlign: 'center', width: '100%' }}
                  >
                    Upload Image
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : showAssetsType === 'teams' ? (
            <Grid container sx={{ minWidth: 400 }}>
              {sortedTeams.map((team) => (
                <Grid
                  item
                  xs={2}
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    addAssetHandler(
                      `https://allstarvotefilesde.blob.core.windows.net/nhl-team-logos/${team.teamId}.svg`,
                      team.teamName,
                      team.teamNameFr,
                      null,
                      team.teamId
                    )
                  }
                >
                  <Avatar
                    sx={{ margin: '0 auto' }}
                    alt='Remy Sharp'
                    src={`https://allstarvotefilesde.blob.core.windows.net/nhl-team-logos/${team.teamId}.svg`}
                  />
                  <Box
                    sx={{
                      fontSize: 8,
                      pl: 2,
                      pr: 2,
                      pb: 1,
                      textAlign: 'center',
                    }}
                  >
                    {team.teamName}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : showAssetsType === 'teams_players' ? (
            <Grid container sx={{ minWidth: 400 }}>
              {sortedTeams.map((team) => (
                <Grid
                  item
                  xs={2}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => loadTeamPlayers(team?.abbr)}
                >
                  <Avatar
                    sx={{ margin: '0 auto' }}
                    alt='Remy Sharp'
                    src={`https://allstarvotefilesde.blob.core.windows.net/nhl-team-logos/${team.teamId}.svg`}
                  />
                  <Box
                    sx={{
                      fontSize: 8,
                      pl: 2,
                      pr: 2,
                      pb: 1,
                      textAlign: 'center',
                    }}
                  >
                    {team.teamName}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : showAssetsType === 'players' ? (
            <Grid container sx={{ minWidth: 400 }}>
              {teamPlayers.Players.map((player: any) => (
                <Grid
                  item
                  xs={2}
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    addAssetHandler(
                      `https://assets.nhle.com/mugs/nhl/20242025/${teamPlayers.Abbr}/${player.PlayerFeedId}.png`,
                      `${player.FirstName} ${player.LastName}`,
                      `${player.FirstName} ${player.LastName}`,
                      player.jersey_num,
                      getTeamId(teamPlayers.Abbr),
                      player.PlayerFeedId
                    )
                  }
                >
                  <Avatar
                    sx={{ margin: '0 auto' }}
                    alt='Remy Sharp'
                    src={`https://assets.nhle.com/mugs/nhl/20242025/${teamPlayers.Abbr}/${player.PlayerFeedId}.png`}
                  />
                  <Box
                    sx={{
                      fontSize: 8,
                      pl: 2,
                      pr: 2,
                      pb: 1,
                      textAlign: 'center',
                    }}
                  >
                    {player.FirstName} {player.LastName}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : showAssetsType === 'image_library' ? (
            <Grid container sx={{ minWidth: 400 }}>
              <ImageList cols={3}>
                {imageLibraryFiles.map((item, index) => (
                  <ImageListItem
                    key={index}
                    onClick={() => addAssetHandler(item.blobUrl)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <img
                      src={item.blobUrl}
                      srcSet={item.blobUrl}
                      alt='library'
                      loading='lazy'
                    />
                    <ImageListItemBar title={item.blobName} subtitle={''} />
                  </ImageListItem>
                ))}
              </ImageList>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCancel()}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
